import React from 'react';
import styled from 'styled-components/macro';
import { useHistory, useLocation } from 'react-router-dom';
import { Col, Button, Space, Badge } from 'antd';
import { InfoCircleOutlined, PartitionOutlined } from '@ant-design/icons';
import { EntityType } from '../../../../../../types.generated';
import { navigateToLineageUrl } from '../../../../../lineage/utils/navigateToLineageUrl';
import { ENTITY_TYPES_WITH_MANUAL_LINEAGE } from '../../../constants';
import useIsLineageMode from '../../../../../lineage/utils/useIsLineageMode';
import { useGetLineageTimeParams } from '../../../../../lineage/utils/useGetLineageTimeParams';
import { useIsSeparateSiblingsMode } from '../../../siblingUtils';
import { useGetLineageCountsQuery } from '../../../../../../graphql/lineage.generated';
import useIsChangeLogMode from '../utils/useIsChangeLogMode';
import { useTranslation } from '../../../../../../i18n/useTranslation';
import { ReactComponent as ChangeLogIcon } from '../../../../../../images/icon-change-log.svg';

import { ReactComponent as UpstreamIcon } from '../../../../../../images/icon-upstream.svg';
import { ReactComponent as DownstreamIcon } from '../../../../../../images/icon-downstream.svg';

const LineageSummary = styled.div`
    margin-left: 0;
`;

const LineageBadge = styled(Badge)`
    display: flex;
    padding: 8px;
    align-items: center;
    border-radius: 32px;
    background-color: #fff;
    border: 1px solid #e0e0e0;
`;

const SpaceStyled = styled(Space)`
    width: 100%;
    justify-content: flex-end;
`;

type Props = {
    urn: string;
    type: EntityType;
};

/**
 * Responsible for rendering a clickable browse path view.
 */
export const LineageSelector = ({ urn, type }: Props): JSX.Element => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const isLineageMode = useIsLineageMode();
    const isHideSiblingsMode = useIsSeparateSiblingsMode();
    const { startTimeMillis, endTimeMillis } = useGetLineageTimeParams();
    const isChangeLogMode = useIsChangeLogMode();

    // Fetch the lineage counts for the entity.
    const { data: lineageData, loading: lineageLoading } = useGetLineageCountsQuery({
        variables: {
            urn,
            separateSiblings: isHideSiblingsMode,
            startTimeMillis,
            endTimeMillis,
        },
    });

    const upstreamTotal = (lineageData?.entity as any)?.upstream?.total || 0;
    const upstreamFiltered = (lineageData?.entity as any)?.upstream?.filtered || 0;
    const upstreamCount = upstreamTotal - upstreamFiltered;

    const downstreamTotal = (lineageData?.entity as any)?.downstream?.total || 0;
    const downstreamFiltered = (lineageData?.entity as any)?.downstream?.filtered || 0;
    const downstreamCount = downstreamTotal - downstreamFiltered;

    const hasLineage = upstreamCount > 0 || downstreamCount > 0;
    const canNavigateToLineage = hasLineage || ENTITY_TYPES_WITH_MANUAL_LINEAGE.has(type);

    const upstreamText = upstreamCount === 100 ? '100+' : upstreamCount;
    const downstreamText = downstreamCount === 100 ? '100+' : downstreamCount;

    const isDataProduct = type === EntityType.DataProduct;

    return (
        <>
            <Col flex="auto">
                <SpaceStyled size="middle" className="f-mt-3-xs">
                    <Button
                        type="text"
                        icon={<ChangeLogIcon />}
                        className={`${isChangeLogMode ? 'f-btn-active' : ''} f-lineage-btn f-btn-flat`}
                        onClick={() => {
                            navigateToLineageUrl({ location, history, isLineageMode: false, isChangeLogMode: true });
                        }}
                    >
                        {t('Change Log')}
                    </Button>
                    <Button
                        type="text"
                        icon={<InfoCircleOutlined aria-hidden="true" />}
                        className={`${
                            !isLineageMode && !isChangeLogMode ? 'f-btn-active' : ''
                        } f-lineage-btn f-btn-flat`}
                        disabled={!canNavigateToLineage && !isDataProduct}
                        onClick={() => {
                            if (canNavigateToLineage || isDataProduct) {
                                navigateToLineageUrl({
                                    location,
                                    history,
                                    isLineageMode: false,
                                    startTimeMillis,
                                    endTimeMillis,
                                });
                            }
                        }}
                    >
                        {t('Details')}
                    </Button>
                    {!isDataProduct && (
                        <Button
                            type="text"
                            disabled={!canNavigateToLineage}
                            icon={<PartitionOutlined aria-hidden="true" />}
                            className={`${isLineageMode ? 'f-btn-active' : ''} f-lineage-btn f-btn-flat`}
                            onClick={() => {
                                if (canNavigateToLineage) {
                                    navigateToLineageUrl({
                                        location,
                                        history,
                                        isLineageMode: true,
                                        startTimeMillis,
                                        endTimeMillis,
                                    });
                                }
                            }}
                        >
                            {t('Lineage')}
                        </Button>
                    )}
                </SpaceStyled>
            </Col>
            {!isDataProduct && (
                <Col flex="none" className="f-mt-3-xs">
                    <LineageSummary>
                        <LineageBadge className="f-text-content f-color-dark-black-s70">
                            <UpstreamIcon className="mr-1" style={{ width: 16, height: 16 }} />
                            <div className="f-color-dark-black-s100 mr-1 ml-2">
                                {lineageLoading ? '-' : upstreamText}
                            </div>
                            <div>{t('Upstream')}</div>
                            <DownstreamIcon className="mr-1 ml-2" style={{ width: 16, height: 16 }} />
                            <div className="f-color-dark-black-s100 mr-1 ml-2">
                                {lineageLoading ? '-' : downstreamText}
                            </div>
                            <div>{t('Downstream')}</div>
                        </LineageBadge>
                    </LineageSummary>
                </Col>
            )}
        </>
    );
};
